import { apiErrorMessage } from '@agos/api-client'
import {
  getGetSuperAdminFranchisesFranchiseIdManagerSettingsQueryKey,
  useGetSettingsFooter,
  useGetSuperAdminFranchisesFranchiseIdManagerSettings,
  usePatchSuperAdminFranchisesFranchiseIdManagerSettings,
} from '@agos/api-client/platform'
import { T, formatCount, tt } from '@agos/shared'
import { PageHeader, cn, toast, ReadFailed, Spinner, StatusBadge } from '@agos/ui'
import { useQueryClient } from '@tanstack/react-query'
import { Save, SlidersHorizontal } from 'lucide-react'
import { useMemo, useState } from 'react'
import { useParams } from 'wouter'

import {
  acceptsWebOrders,
  hoursNotEnforcedNote,
  managerSettingsOf,
  managerSettingsPatch,
  phoneMatchesFooter,
  phoneMismatchNote,
  remoteSwitchNote,
  type ManagerSettings,
  deliveryPlatformKeysOf,
  platformKeyNote,
  SETTINGS_SECTIONS,
  hourlyRatesNote,
  rawSectionNote,
  settingsSectionProblem,
  settingsSectionsOf,
  settingsSectionsPatch,
  unparseableSections,
} from './manager-settings'

/**
 * One franchise's restaurant settings.
 *
 * Shows both phone numbers and says which one customers see (**FB-S04**), and says beside the ordering
 * switches that the opening hours are not a third one (**FB-C06**, from the operator's side).
 */
export function FranchiseManagerSettingsPage() {
  const params = useParams<{ id: string }>()
  const franchiseId = Number(params.id ?? 0)
  const { data, isLoading, isError } = useGetSuperAdminFranchisesFranchiseIdManagerSettings(franchiseId, {
    query: { enabled: Number.isFinite(franchiseId) && franchiseId > 0 },
  })
  /**
   * The customer-facing footer for **this** franchise.
   *
   * `/settings/footer` is tenant-resolved and the tower sends no tenant — deliberately, because
   * `/super-admin/*` is cross-tenant. Without a header it answers 400. `X-Franchise-Id` is the
   * mechanism the platform already has for exactly this: a super-admin inspecting one franchise.
   * It is sent for this call and no other, so the rest of the screen stays cross-tenant.
   */
  const footer = useGetSettingsFooter({
    request: { headers: { 'X-Franchise-Id': String(franchiseId) } },
    query: { enabled: Number.isFinite(franchiseId) && franchiseId > 0 },
  })
  const patch = usePatchSuperAdminFranchisesFranchiseIdManagerSettings()
  const queryClient = useQueryClient()
  const [edits, setEdits] = useState<Partial<ManagerSettings>>({})
  /**
   * The ten raw jsonb sections - §3.99. Kept as **text**, not as parsed objects: what the operator
   * is editing is a document, and parsing on every keystroke would reformat it under the cursor and
   * lose a half-typed key.
   */
  const [sectionEdits, setSectionEdits] = useState<Record<string, string>>({})
  const [openSection, setOpenSection] = useState<string | null>(null)

  const stored = useMemo(() => managerSettingsOf(data), [data])
  // Derived, not seeded in an effect - the same shape `FranchiseDetailPage` settled on, and for the
  // same reason: the payload arrives on a later render and seeding there is a cascading render.
  const settings: ManagerSettings = useMemo(() => ({ ...stored, ...edits }), [stored, edits])
  // §3.58 - read off the response rather than the edit state: these are not editable here.
  const platformKeys = useMemo(() => deliveryPlatformKeysOf(data), [data])
  const storedSections = useMemo(() => settingsSectionsOf(data), [data])
  const sectionText = useMemo(
    () => ({ ...storedSections, ...sectionEdits }),
    [storedSections, sectionEdits],
  )
  const sectionBody = useMemo(
    () => settingsSectionsPatch(sectionText, storedSections),
    [sectionText, storedSections],
  )
  const broken = useMemo(() => unparseableSections(sectionText), [sectionText])
  const body = useMemo(
    () => ({ ...managerSettingsPatch(settings, stored), ...sectionBody }),
    [settings, stored, sectionBody],
  )

  const save = () => {
    void patch
      .mutateAsync({ franchiseId, data: body })
      .then(async () => {
        await queryClient.invalidateQueries({
          queryKey: getGetSuperAdminFranchisesFranchiseIdManagerSettingsQueryKey(franchiseId),
        })
        setEdits({})
        setSectionEdits({})
        toast({ title: tt('Nastavenia uložené') })
      })
      .catch((failure: unknown) =>
        toast({
          title: apiErrorMessage(failure, tt('Uloženie zlyhalo')),
          variant: 'destructive',
        }),
      )
  }

  const toggle = (key: 'maintenanceMode' | 'onlineOrdersEnabled' | 'autoAccept' | 'announcementEnabled') =>
    setEdits((current) => ({ ...current, [key]: !settings[key] }))
  const footerPhone =
    typeof (footer.data as { phone?: unknown } | undefined)?.phone === 'string'
      ? (footer.data as { phone: string }).phone
      : null

  if (isLoading) {
    return (
      <main className="flex justify-center py-16" data-manager-settings>
        <Spinner />
      </main>
    )
  }

  // §3.107 - **an early return, not a banner over the form**, and §3.82 named this shape as the
  // worst of the seven it fixed: *"a settings form drawn with empty boxes does not say we have not
  // been told yet, it says the platform has no billing details."* Every box here is blank when the
  // read fails, so the form would assert that this franchise accepts no web orders, has no
  // delivery, no opening hours and no footer - and a save from that state would write it.
  if (isError) {
    return (
      <main className="space-y-4" data-manager-settings>
        <PageHeader
          title={tt('Nastavenia prevádzky')}
          description={tt('Nastavenia sa nepodarilo načítať.')}
        />
        <ReadFailed marker="manager-settings-error"><T>
            Nastavenia tejto franšízy sa nepodarilo načítať. Prázdny formulár by tvrdil, že nič nie
            je nastavené - preto sa nezobrazuje.
          </T></ReadFailed>
      </main>
    )
  }

  const switchClass = (enabled: boolean) =>
    cn(
      'min-h-[32px] px-3 rounded-lg border text-xs font-semibold',
      enabled
        ? 'border-primary/40 bg-primary/10 text-primary'
        : 'border-border text-muted-foreground',
    )

  return (
    <main className="space-y-4" data-manager-settings>
      <PageHeader
        title={tt('Nastavenia prevádzky')}
        description={
          acceptsWebOrders(settings)
            ? tt('Franšíza prijíma objednávky z webu.')
            : tt('Franšíza neprijíma objednávky z webu.')
        }
        icon={<SlidersHorizontal className="w-5 h-5" />}
        actions={
          <StatusBadge
            label={acceptsWebOrders(settings) ? tt('Prijíma') : tt('Neprijíma')}
            color={acceptsWebOrders(settings) ? 'green' : 'amber'}
          />
        }
      />

      <div
        className="bg-card border border-border rounded-2xl p-5 space-y-2"
        data-ordering-switches
      >
        {/* §3.5 - the switches are editable now (`PATCH …/manager-settings` had no caller), and the
            note beside them is the point: this page is not the one whose restaurant it is. */}
        <p className="text-[11px] text-amber-500" data-remote-note>
          {remoteSwitchNote()}
        </p>
        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            data-toggle-maintenance
            onClick={() => toggle('maintenanceMode')}
            className={switchClass(settings.maintenanceMode)}
          >
            <T>Údržba</T>
          </button>
          <button
            type="button"
            data-toggle-online
            onClick={() => toggle('onlineOrdersEnabled')}
            className={switchClass(settings.onlineOrdersEnabled)}
          >
            <T>Objednávky online</T>
          </button>
          <button
            type="button"
            data-toggle-auto-accept
            onClick={() => toggle('autoAccept')}
            className={switchClass(settings.autoAccept)}
          >
            <T>Automatické prijatie</T>
          </button>
        </div>
        {/* §3.96 - **the colour follows the meaning, not the boolean.** These three were grey prose
          * of the same size and shape, and two of them mean *this restaurant is not taking orders*:
          * a franchise in maintenance read exactly like one that is trading, on the screen support
          * opens to find out which.
          *
          * Maintenance **on** is the warning; online orders and auto-accept **off** are. Written per
          * state rather than derived, because a `state ? green : grey` helper gets maintenance
          * backwards - which is the one that matters most. */}
        <p className="text-xs" data-switch-maintenance={settings.maintenanceMode ? 'on' : 'off'}>
          <T>Údržba</T>:{' '}
          <StatusBadge
            label={settings.maintenanceMode ? tt('zapnutá') : tt('vypnutá')}
            color={settings.maintenanceMode ? 'amber' : 'zinc'}
          />
        </p>
        <p className="text-xs" data-switch-online={settings.onlineOrdersEnabled ? 'on' : 'off'}>
          <T>Objednávky online</T>:{' '}
          <StatusBadge
            label={settings.onlineOrdersEnabled ? tt('zapnuté') : tt('vypnuté')}
            color={settings.onlineOrdersEnabled ? 'green' : 'amber'}
          />
        </p>
        <p className="text-xs" data-switch-auto-accept={settings.autoAccept ? 'on' : 'off'}>
          <T>Automatické prijatie</T>:{' '}
          <StatusBadge
            label={settings.autoAccept ? tt('zapnuté') : tt('vypnuté')}
            color={settings.autoAccept ? 'green' : 'zinc'}
          />
        </p>
        {hoursNotEnforcedNote(settings) !== '' && (
          <p className="text-[11px] text-amber-500" data-hours-note>
            {hoursNotEnforcedNote(settings)}
          </p>
        )}
        <p className="text-[11px] text-muted-foreground" data-accepts-orders={acceptsWebOrders(settings) ? 'yes' : 'no'}>
          {acceptsWebOrders(settings) ? tt('Franšíza prijíma objednávky z webu.') : tt('Franšíza neprijíma objednávky z webu.')}
        </p>
        {/* §3.58 - the two delivery platforms, as presence and never as a value. The Tower shows a
            credential toggle for every other integration and showed none for these, although the
            service already masks them into the right shape. Named rather than read generically,
            because this endpoint also carries `hourlyRates` in plain form. */}
        <p className="text-[11px] text-muted-foreground" data-platform-key="wolt">
          Wolt: {platformKeyNote(platformKeys.wolt)}
        </p>
        <p className="text-[11px] text-muted-foreground" data-platform-key="bolt">
          Bolt: {platformKeyNote(platformKeys.bolt)}
        </p>
      </div>

      {/* §3.88 - **the notice the platform could not put up.** `announcement` is the banner across
          the top of the franchise's customer site - `announcementOf` / `announcementShows` in
          `customer-core/opening.ts`, drawn by the customer `HomePage` - and this screen, whose whole
          purpose is to let support change a franchise's settings without asking somebody at the
          shop, could switch the site into maintenance mode and not say why.

          `TenantManagerSettingsService` already writes **both** places the announcement lives: the
          column and `opening_hours.announcement.message`, which is the one the customer site reads.
          Its own javadoc says *"writing only the column would change nothing anybody can see"* - so
          this reaches the public because the service was already careful, not because the field is
          new.

          The switch is separate from the text on purpose: an empty message and a message switched
          off are different states, and the customer site reads the flag. */}
      <div className="bg-card border border-border rounded-2xl p-5 space-y-3" data-announcement>
        <div className="flex items-center justify-between gap-3">
          <span className="text-xs font-semibold">
            <T>Oznámenie na webe</T>
          </span>
          <button
            type="button"
            data-field="announcementEnabled"
            data-field-on={settings.announcementEnabled ? 'true' : 'false'}
            onClick={() => toggle('announcementEnabled')}
            className={switchClass(settings.announcementEnabled)}
          >
            {settings.announcementEnabled ? tt('Zobrazuje sa') : tt('Skryté')}
          </button>
        </div>
        <textarea
          data-field="announcement"
          aria-label={tt('Oznámenie na webe')}
          rows={2}
          value={settings.announcement ?? ''}
          onChange={(change) => setEdits((current) => ({ ...current, announcement: change.target.value }))}
          placeholder={tt('Napríklad: Zatvorené do pondelka.')}
          className="w-full rounded-xl border border-border bg-background px-3 py-2 text-sm"
        />
        <p className="text-[11px] text-muted-foreground">
          <T>Zobrazí sa zákazníkom hore na stránke tejto franšízy.</T>
        </p>
      </div>

      <div className="bg-card border border-border rounded-2xl p-5 space-y-3" data-contact>
        {/* Kept: it compares the setting against what the footer really shows, which the input
            below cannot say. §3.88 adds the field; the note was never the problem. */}
        <p className="text-xs" data-settings-phone>
          <T>Telefón v nastaveniach</T>: {settings.phoneNumber ?? '-'}
        </p>
        {phoneMismatchNote(settings, footerPhone) !== '' && (
          <p className="text-[11px] text-amber-500" data-phone-mismatch>
            {phoneMismatchNote(settings, footerPhone)}
          </p>
        )}
        {phoneMatchesFooter(settings, footerPhone) && (
          <p className="text-[11px] text-muted-foreground" data-phone-matches>
            <T>Toto číslo vidia aj zákazníci.</T>
          </p>
        )}
        {/* §3.88 - the phone was rendered, discussed and compared against the footer, and none of
            that made it a field. `COLUMNS` has always accepted it. */}
        <label className="block space-y-1">
          <span className="text-[11px] font-semibold text-muted-foreground">
            <T>Telefón (zákaznícky)</T>
          </span>
          <input
            data-field="phoneNumber"
            aria-label={tt('Telefón (zákaznícky)')}
            value={settings.phoneNumber ?? ''}
            onChange={(change) => setEdits((current) => ({ ...current, phoneNumber: change.target.value }))}
            className="w-full min-h-[40px] rounded-xl border border-border bg-background px-3 text-sm"
          />
        </label>
        <label className="block space-y-1">
          <span className="text-[11px] font-semibold text-muted-foreground">
            <T>Adresa v pätke webu</T>
          </span>
          <input
            data-field="footerAddress"
            aria-label={tt('Adresa v pätke webu')}
            value={settings.footerAddress ?? ''}
            onChange={(change) => setEdits((current) => ({ ...current, footerAddress: change.target.value }))}
            className="w-full min-h-[40px] rounded-xl border border-border bg-background px-3 text-sm"
          />
        </label>
      </div>

      {/* ─── The whole configuration, group by group - §3.99 ───────────────────

          **Eleven groups the server has always written and no Tower screen could reach.**
          `TenantManagerSettingsService.BLOBS` names all eleven and legacy's own super-admin has a
          `JSONB_SECTIONS` table over exactly the same eleven, described as a power-user editor. This
          screen had the six scalars above and nothing else, so a super-admin could switch a
          restaurant off and could not correct its depot address.

          **The write path was finished before the screen existed.** `keepingSecrets` opens *"a
          screen that reads a blob and posts it back carries no key at all"* - a description of this
          block, written for a caller nobody had written - so the masked pseudo-keys are stripped and
          an unsent secret keeps its stored value.

          **All eleven, including `hourlyRates`** - §3.109, owner answer to #34: *"Yes, show them like
          the other ten"*. It was held back because it is every employee's pay and §3.58 declined to
          publish this endpoint generically for that reason; the owner whose staff they are said
          otherwise. §3.116 corrected the note underneath, which still said the rates were not shown
          here. */}
      <div className="bg-card border border-border rounded-2xl p-5 space-y-3" data-settings-sections>
        <div className="flex flex-wrap items-baseline gap-2">
          <h2 className="text-xs font-bold text-muted-foreground uppercase tracking-wider">
            <T>Sekcie nastavení</T>
          </h2>
          <span className="text-[11px] text-muted-foreground" data-sections-count>
            {String(SETTINGS_SECTIONS.length)}
          </span>
        </div>
        <p className="text-[11px] text-amber-500" data-sections-note>
          {rawSectionNote()}
        </p>

        {broken.length > 0 && (
          /* Every broken section at once. One at a time would mean ten saves to find out that two
             of them are wrong. */
          <p className="text-xs font-semibold text-destructive" data-sections-broken={String(broken.length)}>
            {broken.map((section) => `${section.label}: ${section.problem}`).join(' ')}
          </p>
        )}

        <div className="divide-y divide-border">
          {SETTINGS_SECTIONS.map((section) => {
            const text = sectionText[section.key] ?? '{}'
            const problem = settingsSectionProblem(text)
            const changed = Object.prototype.hasOwnProperty.call(sectionBody, section.key)
            const open = openSection === section.key
            return (
              <div key={section.key} className="py-2 space-y-2" data-settings-section={section.key}>
                <button
                  type="button"
                  data-section-toggle={section.key}
                  aria-expanded={open}
                  onClick={() => setOpenSection(open ? null : section.key)}
                  className="w-full text-left"
                >
                  <span className="flex flex-wrap items-baseline gap-2">
                    <span className="text-xs font-semibold">{section.label}</span>
                    {changed && (
                      /* Which sections a save will carry, said before pressing it: the whole rule
                         here is that an untouched group is not rewritten. */
                      <span className="text-[10px] font-bold uppercase tracking-wider text-primary" data-section-changed={section.key}>
                        <T>zmenené</T>
                      </span>
                    )}
                    {problem !== '' && (
                      <span className="text-[10px] font-bold uppercase tracking-wider text-destructive">
                        {problem}
                      </span>
                    )}
                    <span className="ml-auto text-[11px] text-muted-foreground">
                      {open ? tt('zavrieť') : tt('otvoriť')}
                    </span>
                  </span>
                  <span className="block text-[11px] text-muted-foreground">
                    {section.description}
                  </span>
                </button>

                {open && (
                  <textarea
                    data-section-editor={section.key}
                    aria-label={section.label}
                    spellCheck={false}
                    rows={Math.min(24, Math.max(4, text.split('\n').length + 1))}
                    value={text}
                    onChange={(change) =>
                      setSectionEdits((current) => ({ ...current, [section.key]: change.target.value }))
                    }
                    className={cn(
                      'w-full rounded-xl border bg-background px-3 py-2 font-mono text-[11px]',
                      problem === '' ? 'border-border' : 'border-destructive',
                    )}
                  />
                )}
              </div>
            )
          })}
        </div>

        <p className="text-[11px] text-muted-foreground" data-hourly-rates-note>
          {hourlyRatesNote()}
        </p>
      </div>

      <div className="flex items-center gap-2">
        <button
          type="button"
          data-settings-save
          // Refused while any section will not parse, rather than sending nine and dropping one:
          // `settingsSectionsPatch` skips a broken section, so the save would report success over a
          // change the operator can see on screen and that never left the browser.
          disabled={Object.keys(body).length === 0 || broken.length > 0 || patch.isPending}
          onClick={save}
          className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-primary/40 bg-primary/10 text-primary text-xs font-semibold disabled:opacity-40"
        >
          <Save className="w-3.5 h-3.5" /> <T>Uložiť nastavenia</T>
        </button>
        {/* §3.113 - legacy's *Zahodiť*, which this page had no counterpart for. On a screen with eleven
            JSON editors and four switches, the only way back from a change made by mistake was a
            reload - and a reload is not obviously safe to somebody who has just typed into a document
            they do not want to lose. Drawn only when there is something to discard. */}
        {Object.keys(body).length > 0 && !patch.isPending && (
          <button
            type="button"
            data-settings-discard
            onClick={() => {
              setEdits({})
              setSectionEdits({})
            }}
            className="px-3 py-1.5 rounded-lg border border-border text-xs font-semibold text-muted-foreground"
          >
            <T>Zahodiť</T>
          </button>
        )}
        {patch.isPending && <Spinner />}
        {/* Only what changed: `update` writes the keys the body names, and two of these stop a
            restaurant taking orders - so an unchanged one riding along would put back a toggle
            somebody flipped in the manager while this page was open.
            §3.113 - said as a sentence rather than as a bare number, which is what legacy does: a
            lone *3* beside a save button is as likely to be read as a version as a change count. */}
        <span className="text-[11px] text-muted-foreground" data-settings-dirty>
          {Object.keys(body).length === 0
            ? tt('Žiadne neuložené zmeny')
            : `${formatCount(Object.keys(body).length)} ${tt('neuložených zmien')}`}
        </span>
      </div>
    </main>
  )
}
